/**
 * Agent fleet registry — shared constants + status machine.
 *
 * Ported from optisigns-devops-controller agent-management. `agents` is the
 * GENERIC fleet registry (Atlas IP whitelisting is control feature #1); these
 * thresholds are shared by api (built pkg), web (source alias), and the P3
 * watchdog scheduler job so the three surfaces can never drift.
 */

/**
 * `pending_removal` is LEGACY DATA, not a state this app produces. The watchdog that
 * wrote it was deleted 2026-08-25 (owner: "too many gates… other logic should be
 * cleaned up") — the two clocks and a human's decision replace it. The value stays in
 * the union because rows written before then still carry it, the 022 CHECK constraint
 * still allows it, and `upsertAgent` still CLEARS it when such a node re-registers.
 * Nothing sets it. Do not build on it.
 */
export type AgentStatus =
  | 'active'
  | 'pending_removal'
  | 'removed'
  | 'removal_failed'
  | 'inactive';

export const AGENT_STATUSES: AgentStatus[] = [
  'active',
  'pending_removal',
  'removed',
  'removal_failed',
  'inactive',
];

/**
 * The fleet-list warning clock — ABSOLUTE MINUTES, deliberately not missed beats.
 *
 * Owner decision (Binh, 2026-08-25): yellow at >10 min of silence, red at >15 min.
 *
 * RED WAS 30, AND IT WAS SHORTENED ON A MEASUREMENT, NOT ON A MODEL. The live fleet was
 * sampled: 134 healthy agents, seconds-since-heartbeat median 130, p95 278, MAX 284. So
 * the real cadence is the agent's own REPORT_INTERVAL of 300 s — the deployed builds do
 * NOT adopt the server's idle `nextPollSeconds` of 60, which the earlier reasoning here
 * assumed they did. Against 300 s:
 *
 *   yellow >10 min = 2 missed beats (2.1x the worst gap a healthy agent has shown)
 *   red    >15 min = 3 missed beats (3.2x that worst gap, 284 s)
 *
 * 15 min is therefore three times further from the noise floor than the noise floor is
 * from zero — far enough not to flap, and half the notice-time the old 30 cost. Yellow
 * stays 10 because 10 IS the floor: anything shorter colours agents that are merely
 * between beats.
 *
 * Why minutes and not beats: the interval is negotiable (`nextPollSeconds`, clamped
 * 5–600 s server-side) even though today's builds ignore it when idle. A threshold
 * expressed in beats goes silently wrong the day anyone turns that dial, or the day a
 * newer agent starts honouring it. Minutes stay meaningful either way — which is exactly
 * why the numbers above are stated as a check on the measurement, never as the definition.
 *
 * THESE ARE THE ONLY TWO CLOCKS. Since 2026-08-25 nothing else in this app answers "is
 * this node alive": the 4-hour reclaim clock, the 30-minute project freshness clock and
 * the 30-day entry-age clock are deleted. Red is what the Overview checklist counts, and
 * a HUMAN decides from there whether to delete the agent or recover the node.
 */
export const AGENT_YELLOW_MINUTES = 10;

/** Red — and the ONLY one of the two that reaches the Overview checklist. Yellow stays
 *  page-local, or the checklist is permanently non-empty and stops being read.
 *
 *  It is also the clock every Atlas-facing read now uses to decide whether an address has
 *  anything alive behind it (`loadAgentIpLiveness`, `listAgentIpRows`, `classifyEntries`,
 *  the cascade preview and the Overview counts). One question, one answer. */
export const AGENT_RED_MINUTES = 15;

/** Fleet-list colour for one agent. There is no fourth level: `red` IS the reclaim
 *  signal, and what happens next is a human's decision, not a further threshold. */
export type AgentStaleLevel = 'green' | 'yellow' | 'red';

/**
 * Which colour an agent's row gets, from its last heartbeat.
 *
 * One function so the fleet list, the Overview checklist and any future report cannot
 * disagree about what "red" means — the same argument `atlasLevel()` makes for capacity.
 */
export function agentStaleLevel(
  lastSeenAt: Date | string,
  now: Date = new Date(),
): AgentStaleLevel {
  const t = typeof lastSeenAt === 'string' ? new Date(lastSeenAt) : lastSeenAt;
  const silentMinutes = (now.getTime() - t.getTime()) / 60_000;
  if (silentMinutes > AGENT_RED_MINUTES) return 'red';
  if (silentMinutes > AGENT_YELLOW_MINUTES) return 'yellow';
  return 'green';
}

/**
 * The most addresses any ONE run may touch on ONE project's access list.
 *
 * A run that can touch an unbounded slice of a production access list is a run whose blast
 * radius nobody can state before clicking. Anything past the cap is reported `deferred_cap`
 * and re-offered on the next run — never silently dropped.
 *
 * Renamed from `AGENT_CASCADE_MAX_PER_RUN` on 2026-08-25 because it now covers all three
 * paths that write to a list, not just the agent-first cascade: `cascade.ts` (delete),
 * `prune.ts` (delete — which had NO cap at all, and whose trigger moved to a 15-minute
 * silence, so a single blip could have offered all 181 of optisigns-prd's entries in one
 * confirm) and `restore.ts` (add). Human-gated is not the same as unbounded.
 */
export const ATLAS_MAX_ADDRESSES_PER_RUN = 25;

/**
 * Why the cascade would not delete a (project, address) pair.
 *
 * `refused` is OUR verdict and is kept separate from `failed` (Atlas said no) for the
 * reason prune.ts already spells out: collapsing them tells an operator an entry is
 * deliberately still there when in fact the delete never landed and must be retried.
 */
/*
 * TWO GATES, and they are the two the owner kept (2026-08-25): we do not delete from a
 * list we could not read (`atlas_state_unknown`), and no run may touch more than
 * ATLAS_MAX_ADDRESSES_PER_RUN addresses on one project (`deferred_cap`).
 *
 * DELETED with the shared-address model: `shared_with_live_agent` (the NAT sibling rule)
 * and `cluster_no_live_agent` (cluster quorum). Measured 2026-08-25 across the whole
 * fleet: 356 agents, 356 distinct public addresses — the shared-address case the first
 * gate existed for has never occurred. The second one froze exactly the case an operator
 * is looking at when a whole cluster goes red, which is now their decision to make from
 * the checklist.
 *
 * The remaining two values are not gates: `not_linked` means the caller named a pair no
 * link row supports, and `open_to_all` is a last-resort assertion that a /0 never reaches
 * the deleter.
 */
export type AgentRemovalRefusal =
  /** A /0. Removing it could cut off everything; it is a security finding, not prune fodder. */
  | 'open_to_all'
  /** No credentials / the list has never been read. We do not delete from a list we
   *  have not looked at. */
  | 'atlas_state_unknown'
  /** Past ATLAS_MAX_ADDRESSES_PER_RUN for this project in this run. */
  | 'deferred_cap'
  /** The caller named a pair with no un-released link row — nothing says this agent
   *  ever put that address on that project. */
  | 'not_linked'
  /** RETAINED, NEVER RETURNED (052 → owner decision e-32). It was v1's answer for a
   *  DigitalOcean target; a do_db delete is now parity with Atlas and only the wire
   *  differs (ONE whole-set PUT instead of one DELETE per address). The literal stays
   *  because the label maps over this union are exhaustive `Record`s. */
  | 'kind_unsupported';
